import { imageRefPrefix, imageStoreBytes, pruneImages, resolveImage, storeImage } from '../utils/imageStore';

export interface WebsiteShow {
  id: string;
  date: string;
  venue: string;
  city: string;
  ticketUrl: string;
  presaleNote: string;
  status: 'on-sale' | 'sold-out' | 'coming-soon';
}

export interface WebsiteRelease {
  id: string;
  eyebrow: string;
  title: string;
  meta: string;
  artworkUrl: string;
  url: string;
  ctaLabel: string;
}

export interface WebsiteMerchItem {
  id: string;
  name: string;
  description: string;
  price: number;
  imageUrl: string;
  checkoutUrl: string;
  available: boolean;
}

export interface WebsiteGalleryImage {
  id: string;
  url: string;
  caption: string;
}

export interface WebsiteMember {
  id: string;
  name: string;
  role: string;
}

export interface WebsiteVenueCredit {
  id: string;
  venue: string;
  detail: string;
  city: string;
}

export interface WebsiteAward {
  id: string;
  label: string;
  title: string;
  detail: string;
  /** Logo of the station, contest, or organization giving the award. */
  logoUrl: string;
  /** Where the logo links to. Optional. */
  logoLinkUrl: string;
}

export interface WebsiteEndorsement {
  id: string;
  artist: string;
  quote: string;
  /** Portrait of the artist giving the quote. Optional — the quote stands alone without it. */
  imageUrl: string;
}

export interface EpkTrack {
  id: string;
  title: string;
  duration: string;
  url: string;
}

export interface WebsiteContent {
  bandName: string;
  heroImageUrl: string;
  heroEyebrow: string;
  heroHeadline: string;
  heroDescription: string;
  heroCtaLabel: string;
  heroCtaUrl: string;
  releaseEyebrow: string;
  releaseTitle: string;
  releaseMeta: string;
  releaseArtworkUrl: string;
  releaseUrl: string;
  releaseCtaLabel: string;
  merchandiseUrl: string;
  merchandiseLabel: string;
  bookingEmail: string;
  tiktokUrl: string;
  instagramUrl: string;
  spotifyUrl: string;
  youtubeUrl: string;
  musicHidden: boolean;
  musicImageUrl: string;
  musicEyebrow: string;
  musicHeadline: string;
  musicDescription: string;
  musicPrimaryLabel: string;
  musicPrimaryUrl: string;
  musicSecondaryLabel: string;
  musicSecondaryUrl: string;
  merchHidden: boolean;
  vipHidden: boolean;
  showsHidden: boolean;
  bandsintownArtist: string;
  bandsintownAppId: string;
  bandsintownAutoSync: boolean;
  galleryHidden: boolean;
  galleryEyebrow: string;
  galleryTitle: string;
  galleryImages: WebsiteGalleryImage[];
  aboutHidden: boolean;
  aboutEyebrow: string;
  aboutTitle: string;
  aboutBody: string;
  aboutImageUrl: string;
  aboutMembers: WebsiteMember[];
  aboutHeroImageUrl: string;
  aboutHeroImageAlt: string;
  aboutImageAlt: string;
  aboutFeatureImageUrl: string;
  aboutFeatureImageAlt: string;
  aboutVenuesIntro: string;
  aboutVenues: WebsiteVenueCredit[];
  aboutSharedStage: string;
  aboutAwards: WebsiteAward[];
  aboutEndorsements: WebsiteEndorsement[];
  aboutCtaHeadline: string;
  aboutCtaBody: string;
  aboutSeoTitle: string;
  aboutSeoDescription: string;
  aboutShareTitle: string;
  aboutShareDescription: string;
  aboutShareImageUrl: string;
  epkHeroImageUrl: string;
  epkHeroImageAlt: string;
  epkEyebrow: string;
  epkBioTitle: string;
  epkBio: string;
  epkQuotes: WebsiteEndorsement[];
  epkPressPhotos: WebsiteGalleryImage[];
  epkTracks: EpkTrack[];
  epkContactName: string;
  epkContactEmail: string;
  epkContactPhone: string;
  epkFactDraw: string;
  seoTitle: string;
  seoDescription: string;
  seoLocation: string;
  seoGenre: string;
  siteUrl: string;
  shareImageUrl: string;
  releases: WebsiteRelease[];
  merchItems: WebsiteMerchItem[];
  shows: WebsiteShow[];
}

export const websiteStorageKey = 'artistos-website-content';
export const websiteUpdatedEvent = 'artistos:website-updated';

/** The staging copy. Edits land here first and stay invisible to fans until published. */
export const websiteDraftStorageKey = 'artistos-website-draft';
export const websiteDraftUpdatedEvent = 'artistos:website-draft-updated';
/** Records when the live version was last promoted from the draft. */
export const websitePublishedAtKey = 'artistos-website-published-at';

export type WebsiteStage = 'draft' | 'live';

/** Reads ?stage=draft so the same public pages can render either version. */
export function getRequestedStage(): WebsiteStage {
  try {
    return new URLSearchParams(window.location.search).get('stage') === 'draft' ? 'draft' : 'live';
  } catch {
    return 'live';
  }
}

export const defaultWebsiteContent: WebsiteContent = {
  bandName: 'COOPER TY BAND',
  heroImageUrl: 'https://cdn.magicpatterns.com/uploads/1wreTzNiWxMCwuqpzrU6qt/image.png',
  heroEyebrow: 'Texas country · Fort Worth, TX',
  heroHeadline: '',
  heroDescription: '',
  heroCtaLabel: '',
  heroCtaUrl: '#shows',
  releaseEyebrow: 'New single',
  releaseTitle: 'Gone Last Week',
  releaseMeta: 'Cooper Ty Band · 2026',
  releaseArtworkUrl: 'https://cdn.magicpatterns.com/uploads/55PpjMwhmz3ZUMQuz9BUEQ/3AE176BF-3876-47EE-AE10-B82013DADEBA.png',
  releaseUrl: 'https://open.spotify.com/album/70VhpntB5E2jMk0yUGpyzQ',
  releaseCtaLabel: 'Listen on Spotify',
  merchandiseUrl: '#',
  merchandiseLabel: 'Shop merch',
  bookingEmail: 'booking@coopertyband.com',
  tiktokUrl: 'https://www.tiktok.com/@coopertyband?lang=en',
  instagramUrl: 'https://www.instagram.com/coopertyband/',
  spotifyUrl: 'https://open.spotify.com/album/70hpntB5E2jMk0yUGpyzQ',
  youtubeUrl: '',
  musicHidden: false,
  musicImageUrl: '',
  musicEyebrow: 'All the music, one click away',
  musicHeadline: 'Listen to Cooper Ty Band',
  musicDescription: 'Every single, every song. Follow along wherever you stream.',
  musicPrimaryLabel: 'Listen on Spotify',
  musicPrimaryUrl: 'https://open.spotify.com/album/70VhpntB5E2jMk0yUGpyzQ',
  musicSecondaryLabel: 'Listen on Apple Music',
  musicSecondaryUrl: 'https://music.apple.com/us/artist/cooper-ty-band/1846754482',
  // Hidden until a real checkout and a real email provider are connected.
  merchHidden: true,
  vipHidden: false,
  showsHidden: false,
  bandsintownArtist: 'id_15664778',
  bandsintownAppId: '60261d4fc3153450383d8bc0102da346',
  bandsintownAutoSync: true,
  galleryHidden: false,
  galleryEyebrow: 'From the road',
  galleryTitle: 'Images from the Road',
  galleryImages: [
    { id: 'road-1', url: 'https://cdn.magicpatterns.com/patterns/generated-images/07c9c950-e37e-4602-b505-f85703705bdb.jpg', caption: 'The White Horse · Austin, TX' },
    { id: 'road-2', url: 'https://cdn.magicpatterns.com/patterns/generated-images/dc24136c-7a93-42f9-b3de-6c6f56074193.jpg', caption: 'Somewhere west of Abilene' },
    { id: 'road-3', url: 'https://cdn.magicpatterns.com/patterns/generated-images/a1a30682-1e90-4cd3-8e52-dca0d44dbc1a.jpg', caption: 'Twenty minutes to downbeat' },
    { id: 'road-4', url: 'https://cdn.magicpatterns.com/patterns/generated-images/7de57262-f6ce-4254-97ff-6132f17ff82f.jpg', caption: 'Dancehall crowd at last light' },
    { id: 'road-5', url: 'https://cdn.magicpatterns.com/patterns/generated-images/3cb4144e-b3cb-4544-a812-8a8159be53db.jpg', caption: 'The guitar that has seen every mile' },
  ],
  aboutHidden: false,
  aboutEyebrow: 'Texas country · Fort Worth, TX',
  aboutTitle: 'About Cooper Ty Band',
  aboutBody:
    'Cooper Ty Band is part of a new wave of Texas country artists blending modern country, Southern rock, blues, and Texas grit into a sound built for the stage.\n\nKnown for gritty guitars, strong songwriting, and high-energy live shows, Cooper Ty Band has quickly grown from playing local bars and college venues to performing on some of Texas’ most recognized stages and opening for artists including Ty Myers and Bottomland.\n\nThe band got its start in College Station, Texas, where Cooper Ty built an early following playing college towns and venues across the state. As the crowds grew and the music gained momentum, the band made the move to Fort Worth, planting its roots in one of the most active Texas country music scenes in the state.\n\nNow based in Fort Worth, Cooper Ty Band is focused full time on the road, the writing room, and the stage.\n\nDrawing influence from classic country, Southern rock, blues, and the modern Texas music scene, Cooper Ty Band delivers music that feels raw, authentic, and unmistakably Texas.',
  aboutImageUrl: 'https://cdn.magicpatterns.com/uploads/mtbHRWyQGAwhaDw29hb3Xi/DSC03838.jpg',
  aboutMembers: [
    { id: 'member-cooper', name: 'Cooper Ty', role: 'Lead vocals, guitar' },
  ],
  aboutHeroImageUrl: '',
  aboutHeroImageAlt: 'Cooper Ty Band performing live on stage in Fort Worth, Texas',
  aboutImageAlt: 'Cooper Ty Band Texas country live performance',
  aboutFeatureImageUrl: '',
  aboutFeatureImageAlt: 'Cooper Ty Band performing at Billy Bob’s Texas in Fort Worth',
  aboutVenuesIntro:
    'Cooper Ty Band has performed throughout Texas at notable venues, arenas, music halls, college venues, and private events.',
  aboutVenues: [
    { id: 'venue-billy-bobs', venue: 'Billy Bob’s Texas', detail: 'Main Stage', city: 'Fort Worth, Texas' },
    { id: 'venue-dickies', venue: 'Dickies Arena', detail: 'Private Performance', city: 'Fort Worth, Texas' },
    { id: 'venue-white-elephant', venue: 'White Elephant Saloon', detail: '', city: 'Fort Worth, Texas' },
    { id: 'venue-love-shack', venue: 'Love Shack', detail: '', city: 'Fort Worth, Texas' },
    { id: 'venue-the-tap', venue: 'The Tap', detail: '', city: 'College Station, Texas' },
    { id: 'venue-rhinestone', venue: 'Rhinestone Saloon', detail: '', city: 'Fort Worth, Texas' },
    { id: 'venue-private', venue: 'Private Events and Performances', detail: '', city: 'Throughout Texas' },
  ],
  aboutSharedStage:
    'Cooper Ty Band has opened for and shared stages with artists including Ty Myers and Bottomland as the band’s presence continues to grow throughout the Texas country music scene.',
  aboutAwards: [
    {
      id: 'award-959',
      label: 'Winner',
      title: '2026 95.9 Singer-Songwriter Contest',
      detail: 'Recognized for original songwriting and live performance.',
      logoUrl:
        'https://dehayf5mhw1h7.cloudfront.net/wp-content/uploads/sites/2740/2025/11/18215415/ranch-logo-mobile-1000x172v2.png',
      logoLinkUrl: '',
    },
  ],
  aboutEndorsements: [
    {
      id: 'quote-usrey',
      artist: 'Tanner Usrey',
      quote: 'He’s a pickin’, singin’ son of a bitch!',
      imageUrl:
        'https://www.musicconnection.com/wp-content/uploads/2023/11/Live_Tanner-Usrey_Credit-Josh-Beech_DSCF1417-1-scaled.jpg',
    },
    {
      id: 'quote-delk',
      artist: 'Tyce Delk',
      quote:
        'There’s something to say about somebody who can sing as well as you and play guitar as well as you, too.',
      imageUrl: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRFEsxmbWE_ipaWVwnsouIm8WIwzOKOjEnothrVVm8ZHQ&s=10',
    },
    {
      id: 'quote-huser',
      artist: 'Huser Brothers Band',
      quote:
        'God almighty, he’s got it goin’ on. With your music, man, you make me thirsty and ready to have a drink. You’re gonna headline Billy Bob’s and sell the most beer in history.',
      imageUrl: 'https://i.scdn.co/image/ab6761610000e5ebc2d2d9d25e92a4af3f5b63bc',
    },
  ],
  aboutCtaHeadline: 'Hear Cooper Ty Band',
  aboutCtaBody: 'Listen to the latest music, see upcoming Texas tour dates, and catch Cooper Ty Band live.',
  aboutSeoTitle: 'Cooper Ty Band | Texas Country Band from Fort Worth, TX',
  aboutSeoDescription:
    'Meet Cooper Ty Band, a Fort Worth Texas country band blending country, Southern rock and blues with gritty songwriting and high-energy live shows.',
  aboutShareTitle: 'Cooper Ty Band | Texas Country Music',
  aboutShareDescription:
    'Meet Cooper Ty Band, a Fort Worth-based Texas country band bringing gritty guitars, authentic songwriting and high-energy live shows to stages across Texas.',
  aboutShareImageUrl: '',
  epkHeroImageUrl: 'https://cdn.magicpatterns.com/uploads/1wreTzNiWxMCwuqpzrU6qt/image.png',
  epkHeroImageAlt: 'Cooper Ty Band performing live in Fort Worth, Texas',
  epkEyebrow: 'Electronic press kit',
  epkBioTitle: 'Texas born, crowd raised',
  epkBio:
    'Cooper Ty Band is part of a new wave of Texas country artists blending country music with southern twang and blues. Built around gritty guitars, strong songwriting, and a reputation for high-energy shows, the band has quickly grown from playing local bars and college venues to opening for acts such as Ty Myers and Bottomland — and playing the main stage at Billy Bob’s Texas.\n\nCooper Ty Band originated in College Station, Texas. After building a strong following and seeing early success playing college towns across the state, the band moved to Fort Worth to fully pursue music, planting itself in one of the state’s most active live country scenes and committing to the road, the writing room, and the stage full time.\n\nDrawing influence from classic country music and Southern rock, they deliver a sound that feels raw, authentic, and built for the stage.',
  epkQuotes: [
    {
      id: 'epk-quote-usrey',
      artist: 'Tanner Usrey',
      quote: 'He’s a pickin’, singin’ son of a bitch!',
      imageUrl:
        'https://www.musicconnection.com/wp-content/uploads/2023/11/Live_Tanner-Usrey_Credit-Josh-Beech_DSCF1417-1-scaled.jpg',
    },
    {
      id: 'epk-quote-delk',
      artist: 'Tyce Delk',
      quote:
        'There’s something to say about somebody that can sing as well as you but also play the guitar as well as you.',
      imageUrl: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRFEsxmbWE_ipaWVwnsouIm8WIwzOKOjEnothrVVm8ZHQ&s=10',
    },
    {
      id: 'epk-quote-huser',
      artist: 'The Huser Brothers Band',
      quote:
        'God almighty, he’s got it goin’ on. With your music, man, you make me thirsty and I’m ready to have a drink. You’re gonna headline Billy Bob’s Texas and sell the most beer in history.',
      imageUrl: 'https://i.scdn.co/image/ab6761610000e5ebc2d2d9d25e92a4af3f5b63bc',
    },
  ],
  epkPressPhotos: [
    {
      id: 'epk-photo-1',
      url: 'https://cdn.magicpatterns.com/uploads/1wreTzNiWxMCwuqpzrU6qt/image.png',
      caption: 'Cooper Ty Band live in Fort Worth, Texas',
    },
    {
      id: 'epk-photo-2',
      url: 'https://cdn.magicpatterns.com/uploads/mtbHRWyQGAwhaDw29hb3Xi/DSC03838.jpg',
      caption: 'Cooper Ty Band Texas country live performance',
    },
  ],
  epkTracks: [
    { id: 'epk-track-1', title: 'Gone Last Week', duration: '3:15', url: 'https://open.spotify.com/album/70VhpntB5E2jMk0yUGpyzQ' },
    { id: 'epk-track-2', title: 'Matagorda Bay', duration: '3:29', url: '' },
    { id: 'epk-track-3', title: 'Lazy Summer Night', duration: '3:49', url: '' },
  ],
  epkContactName: 'Booking · Cooper Ty Band',
  epkContactEmail: 'booking@coopertyband.com',
  epkContactPhone: '',
  epkFactDraw: 'Billy Bob’s Texas · Main Stage',
  seoTitle: 'Cooper Ty Band · Texas Country from Fort Worth',
  seoDescription: 'Official site for Cooper Ty Band. Tour dates, tickets, new music, merch, and booking for the Fort Worth Texas country band.',
  seoLocation: 'Fort Worth, TX',
  seoGenre: 'Texas Country',
  siteUrl: 'https://coopertyband.com',
  shareImageUrl: 'https://cdn.magicpatterns.com/uploads/1wreTzNiWxMCwuqpzrU6qt/image.png',
  releases: [
    { id: 'release-gone-last-week', eyebrow: 'New single', title: 'Gone Last Week', meta: 'Cooper Ty Band · 2026', artworkUrl: 'https://cdn.magicpatterns.com/uploads/55PpjMwhmz3ZUMQuz9BUEQ/3AE176BF-3876-47EE-AE10-B82013DADEBA.png', url: 'https://open.spotify.com/album/70VhpntB5E2jMk0yUGpyzQ', ctaLabel: 'Listen on Spotify' },
  ],
  merchItems: [
    { id: 'merch-road-tee', name: 'Long Way Home Tee', description: 'Washed black cotton tee with a copper desert mark.', price: 32, imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/9f3ebf49-fca0-4a76-b6d9-8c5c75c0214c.jpg', checkoutUrl: '', available: true },
    { id: 'merch-gone-vinyl', name: 'Gone Last Week Vinyl', description: 'Limited 12-inch pressing for the long road home.', price: 28, imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/412a75fe-d6d6-40af-9f8c-e5945293eeb2.jpg', checkoutUrl: '', available: true },
    { id: 'merch-road-cap', name: 'Roadside Cap', description: 'Dark brown trucker cap with a stitched desert patch.', price: 26, imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/4ca6d31e-ecf3-4492-9773-7e3620648cc3.jpg', checkoutUrl: '', available: true },
  ],
  shows: [
    { id: 'show-gruene', date: 'Sep 20, 2026', venue: 'Gruene Hall', city: 'New Braunfels, TX', ticketUrl: '#', presaleNote: 'Fan presale live now', status: 'on-sale' },
    { id: 'show-billy-bobs', date: 'Oct 16, 2026', venue: "Billy Bob’s Texas", city: 'Fort Worth, TX', ticketUrl: '#', presaleNote: 'Tickets on sale Friday', status: 'coming-soon' },
  ],
};

export function getPublishedWebsiteContent(): WebsiteContent {
  try {
    const stored = window.localStorage.getItem(websiteStorageKey);
    if (!stored) return defaultWebsiteContent;
    return unpackContent(normalizeContent({ ...defaultWebsiteContent, ...JSON.parse(stored) } as WebsiteContent));
  } catch {
    return defaultWebsiteContent;
  }
}

/** Brings older saved content up to the current shape. */
function normalizeContent(content: WebsiteContent): WebsiteContent {
  {
    if (content.heroImageUrl === 'https://cdn.magicpatterns.com/uploads/trPUKZajdYNwi99ueQGHuK/EAEC0249-D854-4565-BE58-58707C0B94B0.png') {
      content.heroImageUrl = defaultWebsiteContent.heroImageUrl;
    }
    if (content.heroCtaUrl === '#tour') content.heroCtaUrl = '#shows';
    if (content.heroCtaLabel === 'See tour dates') content.heroCtaLabel = '';
    if (content.heroHeadline === 'Songs for the long way home.') content.heroHeadline = '';
    if (content.heroDescription === 'Honest country music, loud rooms, and stories that stay with you after the lights come up.') content.heroDescription = '';
    if (!Array.isArray(content.releases)) {
      content.releases = [{ id: 'release-gone-last-week', eyebrow: content.releaseEyebrow, title: content.releaseTitle, meta: content.releaseMeta, artworkUrl: content.releaseArtworkUrl, url: content.releaseUrl, ctaLabel: content.releaseCtaLabel }];
    }
    content.releases = content.releases.map((release) => ({ ...release, artworkUrl: release.artworkUrl ?? '' }));
    if (!Array.isArray(content.merchItems)) content.merchItems = defaultWebsiteContent.merchItems;
    if (!Array.isArray(content.galleryImages)) content.galleryImages = defaultWebsiteContent.galleryImages;
    if (!Array.isArray(content.aboutMembers)) content.aboutMembers = defaultWebsiteContent.aboutMembers;
    if (!Array.isArray(content.aboutVenues)) content.aboutVenues = defaultWebsiteContent.aboutVenues;
    if (!Array.isArray(content.epkQuotes)) content.epkQuotes = defaultWebsiteContent.epkQuotes;
    if (!Array.isArray(content.epkPressPhotos)) content.epkPressPhotos = defaultWebsiteContent.epkPressPhotos;
    if (!Array.isArray(content.epkTracks)) content.epkTracks = defaultWebsiteContent.epkTracks;
    if (!Array.isArray(content.aboutAwards)) {
      content.aboutAwards = defaultWebsiteContent.aboutAwards;
    } else {
      content.aboutAwards = content.aboutAwards.map((award) => ({
        ...award,
        logoUrl: award.logoUrl ?? defaultWebsiteContent.aboutAwards.find((seed) => seed.id === award.id)?.logoUrl ?? '',
        logoLinkUrl: award.logoLinkUrl ?? '',
      }));
    }
    if (!Array.isArray(content.aboutEndorsements)) {
      content.aboutEndorsements = defaultWebsiteContent.aboutEndorsements;
    } else {
      content.aboutEndorsements = content.aboutEndorsements.map((entry) => ({
        ...entry,
        imageUrl:
          entry.imageUrl ??
          defaultWebsiteContent.aboutEndorsements.find((seed) => seed.id === entry.id)?.imageUrl ??
          '',
      }));
    }
    return content;
  }
}

/** Every place a photo can live in the site content. */
function mapImageFields(content: WebsiteContent, transform: (value: string) => string): WebsiteContent {
  const single: (keyof WebsiteContent)[] = [
    'heroImageUrl',
    'releaseArtworkUrl',
    'musicImageUrl',
    'shareImageUrl',
    'aboutImageUrl',
    'aboutHeroImageUrl',
    'aboutFeatureImageUrl',
    'aboutShareImageUrl',
    'epkHeroImageUrl',
  ];

  const next = { ...content } as WebsiteContent;
  single.forEach((key) => {
    const value = next[key];
    if (typeof value === 'string') (next[key] as string) = transform(value);
  });

  next.galleryImages = content.galleryImages.map((image) => ({ ...image, url: transform(image.url) }));
  next.merchItems = content.merchItems.map((item) => ({ ...item, imageUrl: transform(item.imageUrl) }));
  next.releases = content.releases.map((release) => ({ ...release, artworkUrl: transform(release.artworkUrl ?? '') }));
  next.aboutAwards = content.aboutAwards.map((award) => ({ ...award, logoUrl: transform(award.logoUrl ?? '') }));
  next.aboutEndorsements = content.aboutEndorsements.map((entry) => ({
    ...entry,
    imageUrl: transform(entry.imageUrl ?? ''),
  }));
  next.epkQuotes = content.epkQuotes.map((entry) => ({ ...entry, imageUrl: transform(entry.imageUrl ?? '') }));
  next.epkPressPhotos = content.epkPressPhotos.map((photo) => ({ ...photo, url: transform(photo.url) }));

  return next;
}

/** Swaps uploaded photos for references so draft and live never duplicate them. */
function packContent(content: WebsiteContent): WebsiteContent {
  return mapImageFields(content, storeImage);
}

function unpackContent(content: WebsiteContent): WebsiteContent {
  return mapImageFields(content, resolveImage);
}

function collectImageRefs(content: WebsiteContent): string[] {
  const refs: string[] = [];
  mapImageFields(content, (value) => {
    if (value.startsWith(imageRefPrefix)) refs.push(value);
    return value;
  });
  return refs;
}

/** The working copy. Falls back to whatever is live the first time you edit. */
export function getDraftWebsiteContent(): WebsiteContent {
  try {
    const stored = window.localStorage.getItem(websiteDraftStorageKey);
    if (!stored) return getPublishedWebsiteContent();
    return unpackContent(normalizeContent({ ...defaultWebsiteContent, ...JSON.parse(stored) } as WebsiteContent));
  } catch {
    return getPublishedWebsiteContent();
  }
}

export function saveDraftWebsiteContent(content: WebsiteContent): boolean {
  try {
    window.localStorage.setItem(websiteDraftStorageKey, JSON.stringify(packContent(content)));
    window.dispatchEvent(new CustomEvent(websiteDraftUpdatedEvent, { detail: content }));
    if ('BroadcastChannel' in window) {
      const channel = new BroadcastChannel('artistos-website-draft');
      channel.postMessage(content);
      channel.close();
    }
    scheduleDraftPush(content);
    return true;
  } catch {
    return false;
  }
}

/** Throws the draft away and returns to exactly what fans currently see. */
export function discardWebsiteDraft(): WebsiteContent {
  try {
    window.localStorage.removeItem(websiteDraftStorageKey);
  } catch {
    // Nothing to discard.
  }
  const live = getPublishedWebsiteContent();
  window.dispatchEvent(new CustomEvent(websiteDraftUpdatedEvent, { detail: live }));
  return live;
}

export function hasUnpublishedChanges(draft: WebsiteContent): boolean {
  return JSON.stringify(draft) !== JSON.stringify(getPublishedWebsiteContent());
}

export function getLastPublishedAt(): string | null {
  try {
    return window.localStorage.getItem(websitePublishedAtKey);
  } catch {
    return null;
  }
}

/** Plain-language list of what differs between the draft and the live site. */
export function describeDraftChanges(draft: WebsiteContent): string[] {
  const live = getPublishedWebsiteContent();
  const changed: string[] = [];
  const differs = (key: keyof WebsiteContent) => JSON.stringify(draft[key]) !== JSON.stringify(live[key]);

  const groups: { label: string; keys: (keyof WebsiteContent)[] }[] = [
    { label: 'Homepage', keys: ['bandName', 'heroImageUrl', 'heroEyebrow', 'heroHeadline', 'heroDescription', 'heroCtaLabel', 'heroCtaUrl'] },
    { label: 'Social links', keys: ['tiktokUrl', 'instagramUrl', 'spotifyUrl', 'youtubeUrl'] },
    { label: 'Music', keys: ['musicHidden', 'musicImageUrl', 'musicEyebrow', 'musicHeadline', 'musicDescription', 'musicPrimaryLabel', 'musicPrimaryUrl', 'musicSecondaryLabel', 'musicSecondaryUrl'] },
    { label: 'Merch store', keys: ['merchHidden', 'merchItems', 'merchandiseUrl', 'merchandiseLabel'] },
    { label: 'VIP signup', keys: ['vipHidden'] },
    { label: 'Tour dates', keys: ['showsHidden', 'shows', 'bandsintownArtist', 'bandsintownAppId', 'bandsintownAutoSync'] },
    { label: 'Images from the Road', keys: ['galleryHidden', 'galleryEyebrow', 'galleryTitle', 'galleryImages'] },
    { label: 'About page', keys: ['aboutHidden', 'aboutEyebrow', 'aboutTitle', 'aboutBody', 'aboutImageUrl', 'aboutMembers', 'aboutHeroImageUrl', 'aboutFeatureImageUrl', 'aboutVenues', 'aboutVenuesIntro', 'aboutSharedStage', 'aboutAwards', 'aboutEndorsements', 'aboutCtaHeadline', 'aboutCtaBody'] },
    { label: 'Press kit', keys: ['epkHeroImageUrl', 'epkEyebrow', 'epkBioTitle', 'epkBio', 'epkQuotes', 'epkPressPhotos', 'epkTracks', 'epkContactName', 'epkContactEmail', 'epkContactPhone', 'epkFactDraw'] },
    { label: 'Contact', keys: ['bookingEmail'] },
    { label: 'Search & sharing', keys: ['seoTitle', 'seoDescription', 'seoLocation', 'seoGenre', 'siteUrl', 'shareImageUrl', 'aboutSeoTitle', 'aboutSeoDescription', 'aboutShareTitle', 'aboutShareDescription', 'aboutShareImageUrl'] },
  ];

  groups.forEach((group) => {
    if (group.keys.some(differs)) changed.push(group.label);
  });

  return changed;
}

/** Opens the real public site rendered from the draft, so it can be clicked through properly. */
export function draftPreviewUrl(path: '/' | '/about' | '/epk' = '/'): string {
  return `${path}?stage=draft`;
}

export function createWebsitePreviewUrl(content: WebsiteContent): string {
  const bytes = new TextEncoder().encode(JSON.stringify(content));
  // Spreading the whole array into fromCharCode overflows the call stack once
  // uploaded images (stored as data URLs) push the payload past ~100kB.
  let binary = '';
  const chunkSize = 8192;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }
  const payload = btoa(binary);
  return `${window.location.origin}/website#preview=${encodeURIComponent(payload)}`;
}

export function getWebsiteContentFromUrl(): WebsiteContent | null {
  try {
    const payload = new URLSearchParams(window.location.hash.slice(1)).get('preview');
    if (!payload) return null;
    const bytes = Uint8Array.from(atob(payload), (character) => character.charCodeAt(0));
    return { ...defaultWebsiteContent, ...JSON.parse(new TextDecoder().decode(bytes)) } as WebsiteContent;
  } catch {
    return null;
  }
}

/**
 * Downloads the whole site as a single backup file. Content lives in the cloud
 * now, so this is a safety net rather than the only copy — useful before a big
 * round of edits, or to keep an archive of a version you might want back.
 */
export function downloadWebsiteBackup(content: WebsiteContent): void {
  const stamp = new Date().toISOString().slice(0, 10);
  const blob = new Blob([JSON.stringify(content, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `coopertyband-site-${stamp}.json`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * A pasteable, text-only version of the site content.
 *
 * Chat and email reject .json files, so this is how content gets handed over to
 * be written permanently into the code. Uploaded photos are omitted — they are
 * megabytes of encoded data and have to be re-supplied as hosted links anyway.
 */
export function buildContentHandoff(content: WebsiteContent): string {
  const clean = mapImageFields(content, (value) =>
    value.startsWith('data:') ? '[uploaded photo — needs a hosted URL]' : value,
  );
  return JSON.stringify(clean, null, 2);
}

/** Reads a backup file produced by downloadWebsiteBackup and merges it over the defaults. */
export async function readWebsiteBackup(file: File): Promise<WebsiteContent> {
  const text = await file.text();
  const parsed: unknown = JSON.parse(text);
  if (!parsed || typeof parsed !== 'object' || !('bandName' in parsed)) {
    throw new Error('That file is not a site backup.');
  }
  return { ...defaultWebsiteContent, ...(parsed as Partial<WebsiteContent>) } as WebsiteContent;
}

/** Browsers cap localStorage near 5 MB; uploaded photos are what fill it. */
export const websiteStorageLimitBytes = 5_000_000;

export function getWebsiteStorageUsage(content: WebsiteContent): { bytes: number; percent: number } {
  // Photos are stored once and shared by the draft and live copies, so count them once.
  const bytes = imageStoreBytes() + JSON.stringify(mapImageFields(content, (value) => (value.startsWith('data:') ? '' : value))).length * 2;
  return { bytes, percent: Math.min(100, Math.round((bytes / websiteStorageLimitBytes) * 100)) };
}

export function publishWebsiteContent(content: WebsiteContent): boolean {
  try {
    const packed = packContent(content);
    const serialized = JSON.stringify(packed);
    window.localStorage.setItem(websiteStorageKey, serialized);
    window.localStorage.setItem(websitePublishedAtKey, new Date().toISOString());
    window.localStorage.setItem(websiteDraftStorageKey, serialized);
    pruneImages(collectImageRefs(packed));
    window.dispatchEvent(new CustomEvent(websiteUpdatedEvent, { detail: content }));
    if ('BroadcastChannel' in window) {
      const channel = new BroadcastChannel('artistos-website-content');
      channel.postMessage(content);
      channel.close();
    }
    void pushToCloud('live', content);
    return true;
  } catch {
    return false;
  }
}

/* ------------------------------------------------------------------ *
 * Cloud storage
 *
 * The browser copy above is now a fast local cache. Supabase holds the
 * real draft and live versions, which is what makes an edit on one
 * device show up on every other device — and for every fan.
 * ------------------------------------------------------------------ */

/** Fired after content is pulled down from the cloud. */
export const websiteCloudLoadedEvent = 'artistos:website-cloud-loaded';

/**
 * Uploads any photo still held in this browser and swaps it for its public
 * address, so content sent to the cloud never contains a local-only reference
 * that other devices could not resolve.
 */
export async function hostLocalImages(content: WebsiteContent): Promise<WebsiteContent> {
  const locals = new Set<string>();
  mapImageFields(content, (value) => {
    if (value.startsWith('data:') || value.startsWith(imageRefPrefix)) locals.add(value);
    return value;
  });
  if (!locals.size) return content;

  const { uploadPhotoDataUrl } = await import('../utils/photoUpload');
  const hosted = new Map<string, string>();
  for (const value of locals) {
    const dataUrl = value.startsWith(imageRefPrefix) ? resolveImage(value) : value;
    if (!dataUrl) continue;
    const result = await uploadPhotoDataUrl(dataUrl);
    if (result.url) hosted.set(value, result.url);
  }
  if (!hosted.size) return content;
  return mapImageFields(content, (value) => hosted.get(value) ?? value);
}

async function pushToCloud(stage: 'draft' | 'live', content: WebsiteContent): Promise<string | null> {
  const { isCloudConfigured } = await import('./supabase');
  if (!isCloudConfigured()) return null;

  const { pushCloudContent } = await import('./cloudContent');
  const { setCloudStatus } = await import('./cloudStatus');
  setCloudStatus('saving');

  const hostedContent = await hostLocalImages(content);
  // Photos that just moved to the cloud are written back locally so this
  // browser stops holding a second copy of them.
  if (hostedContent !== content) {
    const key = stage === 'live' ? websiteStorageKey : websiteDraftStorageKey;
    try {
      window.localStorage.setItem(key, JSON.stringify(packContent(hostedContent)));
    } catch {
      // The cloud copy is what matters now.
    }
  }

  const error = await pushCloudContent(stage, hostedContent as unknown as Record<string, unknown>);
  setCloudStatus(error ? 'error' : 'synced', error ?? '');
  return error;
}

let draftPushTimer: number | null = null;
let pendingDraft: WebsiteContent | null = null;

/** Draft edits save on every keystroke, so cloud writes are batched. */
function scheduleDraftPush(content: WebsiteContent) {
  pendingDraft = content;
  if (draftPushTimer !== null) window.clearTimeout(draftPushTimer);
  draftPushTimer = window.setTimeout(() => {
    draftPushTimer = null;
    const next = pendingDraft;
    pendingDraft = null;
    if (next) void pushToCloud('draft', next);
  }, 1200);
}

function applyCloudRecord(stage: 'draft' | 'live', raw: Record<string, unknown>, updatedAt: string) {
  const content = normalizeContent({ ...defaultWebsiteContent, ...raw } as WebsiteContent);
  const key = stage === 'live' ? websiteStorageKey : websiteDraftStorageKey;
  try {
    window.localStorage.setItem(key, JSON.stringify(packContent(content)));
    if (stage === 'live' && updatedAt) window.localStorage.setItem(websitePublishedAtKey, updatedAt);
  } catch {
    // Rendering from the fetched copy still works without the cache.
  }
  const resolved = unpackContent(content);
  window.dispatchEvent(
    new CustomEvent(stage === 'live' ? websiteUpdatedEvent : websiteDraftUpdatedEvent, { detail: resolved }),
  );
}

let syncStarted = false;

/**
 * Pulls the site down from the cloud, then keeps it fresh. Called once when the
 * app starts. If the cloud cannot be reached the cached copy keeps rendering,
 * so a visitor never lands on an empty page.
 */
export function startWebsiteCloudSync() {
  if (syncStarted) return;
  syncStarted = true;

  const refresh = async () => {
    const { isCloudConfigured } = await import('./supabase');
    if (!isCloudConfigured()) return;
    const { fetchCloudContent } = await import('./cloudContent');
    const { setCloudStatus } = await import('./cloudStatus');

    const live = await fetchCloudContent('live');
    if (live) applyCloudRecord('live', live.content, live.updatedAt);

    const draft = await fetchCloudContent('draft');
    if (draft) applyCloudRecord('draft', draft.content, draft.updatedAt);

    if (live || draft) {
      setCloudStatus('synced');
      window.dispatchEvent(new CustomEvent(websiteCloudLoadedEvent, { detail: getDraftWebsiteContent() }));
    } else {
      setCloudStatus('local-only', 'Nothing saved to the cloud yet — this device is still the only copy.');
    }
  };

  void refresh();
  window.addEventListener('focus', () => void refresh());
  window.setInterval(() => void refresh(), 45_000);
}

/** Copies everything held in this browser up to the cloud, photos included. */
export async function moveLocalContentToCloud(): Promise<{ photos: number; error?: string }> {
  const { supabase } = await import('./supabase');
  const { data } = await supabase.auth.getSession();
  if (!data.session) {
    return {
      photos: 0,
      error:
        'You are not signed in to the cloud yet. Sign out, then sign back in with the link emailed to you — the database only accepts changes from a verified owner.',
    };
  }

  const draft = getDraftWebsiteContent();
  let photos = 0;
  mapImageFields(draft, (value) => {
    if (value.startsWith('data:') || value.startsWith(imageRefPrefix)) photos += 1;
    return value;
  });

  const hosted = await hostLocalImages(draft);
  const { pushCloudContent } = await import('./cloudContent');
  const record = hosted as unknown as Record<string, unknown>;

  const draftError = await pushCloudContent('draft', record);
  if (draftError) return { photos, error: draftError };

  const liveError = await pushCloudContent('live', record);
  if (liveError) return { photos, error: liveError };

  publishWebsiteContent(hosted);
  return { photos };
}
